import * as React from "react"
import { Info } from "@phosphor-icons/react"
import { cn } from "../../../lib/utils"
import { useObjectResolver } from "./use-object-resolver"
import type { ObjectNameProps } from "./types"
import { Avatar, AvatarFallback, AvatarImage } from "../avatar"

/**
 * Format a phone number for display
 */
function formatPhone(phone: string): string {
  const cleaned = phone.replace(/\D/g, "")
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
  }
  if (cleaned.length === 11 && cleaned.startsWith("1")) {
    return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`
  }
  return phone
}

/**
 * Get display name from an object using fallback chain
 */
function getDisplayName(obj: any, errorMessage?: string): string {
  if (obj.name) {
    return obj.name
  }
  if (obj.display_name) {
    return obj.display_name
  }
  if (obj.title) {
    return obj.title
  }
  if (obj.given_name || obj.family_name) {
    const parts = [obj.given_name, obj.family_name].filter(Boolean)
    return parts.join(" ")
  }
  if (obj.phone) {
    return formatPhone(obj.phone)
  }
  if (obj.email) {
    return obj.email
  }
  return errorMessage || "Name Not Found"
}

/**
 * Get initials from an object for avatar fallback
 */
function getInitials(obj: any): string {
  if (obj.given_name && obj.family_name) {
    return `${obj.given_name[0]}${obj.family_name[0]}`.toUpperCase()
  }
  if (obj.name) {
    const parts = obj.name.split(" ")
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    }
    return obj.name.slice(0, 2).toUpperCase()
  }
  if (obj.email) {
    return obj.email.slice(0, 2).toUpperCase()
  }
  return "??"
}

/**
 * Loading dots component
 */
function LoadingDots() {
  return (
    <span className="inline-flex items-center gap-0.5">
      <span className="h-1 w-1 animate-bounce rounded-full bg-muted-foreground [animation-delay:-0.3s]" />
      <span className="h-1 w-1 animate-bounce rounded-full bg-muted-foreground [animation-delay:-0.15s]" />
      <span className="h-1 w-1 animate-bounce rounded-full bg-muted-foreground" />
    </span>
  )
}

/**
 * ObjectName component - resolves an object ID to its display name.
 *
 * Uses a shared batch queue to efficiently fetch multiple objects in a single API call.
 * Supports avatar display, custom formatters, and name trimming.
 *
 * @example
 * // Basic usage
 * <ObjectName collection="contacts" id="abc123" />
 *
 * // With trimming
 * <ObjectName collection="matters" id="xyz789" trim={25} />
 *
 * // With avatar
 * <ObjectName collection="users" id="user123" showAvatar />
 *
 * // With custom formatter
 * <ObjectName
 *   collection="contacts"
 *   id="abc123"
 *   formatter={(obj) => <strong>{obj.display_name}</strong>}
 * />
 */
export const ObjectName = React.memo(function ObjectName({
  collection,
  id,
  errorMessage,
  formatter,
  trim,
  showAvatar,
  avatarSize = 35,
  className,
}: ObjectNameProps) {
  const { object, isLoading, isError } = useObjectResolver(collection, id)

  // Error state
  if (isError) {
    return (
      <span className={cn("inline-flex items-center text-muted-foreground", className)}>
        <Info className="mr-1.5 h-4 w-4 text-primary" />
        {errorMessage || "Item Not Found"}
      </span>
    )
  }

  // Loading state
  if (isLoading) {
    return (
      <span className={className}>
        <LoadingDots />
      </span>
    )
  }

  // Object found - use custom formatter if provided
  if (formatter && object) {
    return <>{formatter(object)}</>
  }

  // Avatar mode
  if (showAvatar && object) {
    return (
      <Avatar
        className={className}
        style={{ width: avatarSize, height: avatarSize }}
      >
        {object.avatar && <AvatarImage src={object.avatar} alt={getDisplayName(object)} />}
        <AvatarFallback className="text-xs font-semibold">
          {getInitials(object)}
        </AvatarFallback>
      </Avatar>
    )
  }

  // Default text display
  if (object) {
    let displayName = getDisplayName(object, errorMessage)

    // Apply trimming if specified
    if (trim && displayName.length > trim) {
      displayName = displayName.slice(0, trim) + "..."
    }

    return <span className={className}>{displayName}</span>
  }

  return null
})
